import {useEffect, useState} from 'react';
import {Box, Card} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import {UserCaregiverForm} from '../../../../../../../components/user-form/user-caregiver-form';
import {SubmitButtonState} from '../../../../../../../components/submit-button/submit-button';
import {LoadingContent} from '../../../../../../../components/loading-content/loading-content';
import {SectionTabs} from '../../../../../../../components/section-tabs/section-tabs';
import {UserAffiliationRolesTable} from '../../../../../../../components/user-affiliation-roles-table/user-affiliation-roles-table';
import {useOffice} from '../../../../../../../contexts/office/context';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {UserFormType} from '../../../../../../../enums/user-form-type';
import {translations} from '../../../../../../../i18n/translations';
import {EntitiesRepository} from '../../../../../../../repositories/entities';

enum ProfessionalCaregiverTabs {
  Profile = 'profile',
  Affiliations = 'affiliations',
}

export default function EntityProfessionalCaregiverProfileSystemAdministratorOfficeScreen() {
  const {id: entityId, professionalId}: {id?: string; professionalId?: string} = useParams();
  const {path, url} = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const {t} = useTranslation();
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const [isLoading, setIsLoading] = useState(true);
  const redirectUrl = url.slice(0, url.lastIndexOf('/'));
  const profileUrl = `${url}/${ProfessionalCaregiverTabs.Profile}`;
  const affiliationsUrl = `${url}/${ProfessionalCaregiverTabs.Affiliations}`;
  const user = officeContext.state.userForm?.user;
  const currentTab = matchPath(location.pathname, {
    path: affiliationsUrl,
    exact: true,
    strict: false,
  })
    ? ProfessionalCaregiverTabs.Affiliations
    : ProfessionalCaregiverTabs.Profile;

  useEffect(() => {
    let isCurrent = true;

    const getProfessional = async () => {
      setIsLoading(true);
      if (!entityId || !professionalId) {
        history.push(redirectUrl);
        return;
      }

      try {
        const professional = await EntitiesRepository.instance.getAffiliation(
          entityId,
          professionalId
        );

        if (!professional) {
          throw new Error('Empty professional affiliation.');
        }

        if (isCurrent) {
          officeContext.setUserForm(
            professional.user,
            {
              ...professional.affiliation,
              rolesIds: professional.affiliation.rolesIds ?? [],
            },
            professional.affiliationQuickAccess
          );
          setIsLoading(false);
        }
      } catch (error) {
        console.debug('🔴 Could not get professional affiliation.', error);

        if (isCurrent) {
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.caregiver_tabs.errors.caregiver_does_not_exist_error),
          });
          history.push(redirectUrl);
        }
      }
    };

    getProfessional();

    return () => {
      isCurrent = false;
      officeContext.clearUserForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, professionalId]);

  if (isLoading) {
    return <LoadingContent marginTop="24px" />;
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
      <SectionTabs
        title={user?.displayName || user?.name || ''}
        titleAvatar={{type: 'user', url: user?.picture}}
        currentTab={currentTab}
        tabs={[
          {
            value: ProfessionalCaregiverTabs.Profile,
            label: t(translations.caregiver_tabs.tabs.profile),
            href: profileUrl,
          },
          {
            value: ProfessionalCaregiverTabs.Affiliations,
            label: t(translations.caregiver_tabs.tabs.affiliations),
            href: affiliationsUrl,
          },
        ]}
        exitUrl={redirectUrl}
        disabled={
          officeContext.state.userFormDeleteButtonState === SubmitButtonState.Loading ||
          officeContext.state.userFormSubmitButtonState === SubmitButtonState.Loading
        }
      />

      <Switch>
        <Route exact path={`${path}/${ProfessionalCaregiverTabs.Profile}`}>
          <Card
            sx={{
              padding: '40px 0 51px',
              minHeight: 'calc(100vh - 280px)',
              borderRadius: '10px',
            }}
            elevation={0}
          >
            <UserCaregiverForm
              userFormType={UserFormType.EditCaregiver_SystemAdministrator}
              canEditRole
              entityId={entityId}
              redirectUrl={redirectUrl}
            />
          </Card>
        </Route>

        <Route exact path={`${path}/${ProfessionalCaregiverTabs.Affiliations}`}>
          <UserAffiliationRolesTable userId={professionalId || ''} compact />
        </Route>

        <Route exact path={path}>
          <Redirect to={profileUrl} />
        </Route>

        <Route path={path}>
          <Redirect to={profileUrl} />
        </Route>
      </Switch>
    </Box>
  );
}
